import { useTheme } from "@emotion/react";
import { createStackNavigator } from "@react-navigation/stack";
import { cloneDeep } from "lodash";
import React, {
    useContext,
    useEffect,
    useReducer,
    useRef,
    useState,
} from "react";
import { Animated, Image, Platform } from "react-native";
import DrawerButton from "../../components/DrawerButton";
import CloseButton from "../../components/CloseButton";
import { InlineRichText } from "../../components/RichText";
import {
    getDefaultOptionsForType,
    QuestionType,
    useQuiz,
} from "../../utils/contentful";
import { FurnitureContext } from "../../utils/furniture";
import { isAvailable, logicStateReducer } from "../../utils/logic";
import { useExternalState } from "../../utils/me";
import {
    QuizContext,
    QuizType,
    LogicStateContext,
    defaultLogicState,
    defaultQuizState,
} from "../../utils/quiz";
import QuizIntro from "../QuizIntro";
import QuizQuestion from "../QuizQuestion";
import QuizReview from "../QuizReview";
import {
    useSafeAreaFrame,
    SafeAreaProvider,
} from "react-native-safe-area-context";
import { Background, Container, Header, Title } from "./styles";
import * as drawer from "../../utils/drawer";

const Stack = createStackNavigator();

enum ActionType {
    Init = "INIT", // initialize state from firestore
    Load = "LOAD", // "loads" next question
    Start = "START", // starts quiz
    SubmitResponse = "SUBMIT_RESPONSE", // saves a question response
    Next = "NEXT", // updates activeQuestionIndex
    Previous = "PREVIOUS", // decrements activeQuestionIndex
    Resume = "RESUME", // unused?
    UpdateActiveQuestionIndex = "UPDATE_ACTIVE_QUESTION_INDEX", // unused?
}

type Action = {
    type: ActionType;
    quiz?: any;
    questionId?: string;
    questionIndex?: number;
    optionId?: string;
    optionIds?: string[];
    logicState?: any;
    responses?: any;
    previousQuestionIndex?: number;
    activeQuestionIndex?: number;
    isComplete?: boolean;
    dateCompleted?: string;
};

const INTERSTITIAL_RESPONSE = "INTERSTITIAL_CONTINUE";

const reducer = (
    state,
    {
        type,
        quiz,
        questionId,
        optionIds,
        questionIndex,
        logicState,
        responses,
        activeQuestionIndex,
        previousQuestionIndex,
        isComplete,
        dateCompleted,
    }: Action,
) => {
    const activeQuestion =
        state.availableQuestions?.[state.activeQuestionIndex];
    const activeQuestionId = activeQuestion?._id;
    let nextState = state;

    switch (type) {
        case ActionType.Init: {
            nextState = {
                ...state,
                responses,
                activeQuestionIndex,
                isComplete,
                dateCompleted,
            };
            break;
        }
        case ActionType.Load: {
            if (quiz) {
                const availableQuestions = quiz.questions.filter(question => {
                    const result = isAvailable(question, logicState);

                    return result;
                });

                const questionDisplayCount = availableQuestions.reduce(
                    (acc, _question) => {
                        if (_question.type === QuestionType.Interstitial) {
                            return acc;
                        }

                        return acc + 1;
                    },
                    0,
                );

                nextState = {
                    ...state,
                    quizId: quiz._id,
                    availableQuestions,
                    questionDisplayCount,
                };
            }

            break;
        }

        case ActionType.Start: {
            nextState = {
                ...state,
                activeQuestionIndex: 0,
                previousQuestionIndex: -1,
            };

            break;
        }

        case ActionType.SubmitResponse: {
            let { responses, isComplete, availableQuestions } = state;

            if (optionIds && questionId === activeQuestionId) {
                const response = {
                    questionId,
                    optionIds,
                };

                responses = cloneDeep(responses);
                responses[questionId] = response;

                const unansweredQuestions = availableQuestions.filter(
                    _question => !responses[_question._id],
                );
                isComplete = unansweredQuestions.length === 0;
            }

            nextState = {
                ...state,
                responses,
                isComplete,
            };

            break;
        }

        case ActionType.Next: {
            let { responses, activeQuestionIndex, previousQuestionIndex } =
                state;

            if (responses[activeQuestion?._id]) {
                previousQuestionIndex = activeQuestionIndex;
                activeQuestionIndex += 1;
            }

            nextState = {
                ...state,
                activeQuestionIndex,
                previousQuestionIndex,
            };

            break;
        }

        case ActionType.Previous: {
            const { activeQuestionIndex } = state;
            const previous =
                activeQuestionIndex > -1 ? activeQuestionIndex - 1 : -1;

            nextState = {
                ...state,
                activeQuestionIndex: previous,
                previousQuestionIndex: activeQuestionIndex,
            };

            break;
        }

        case ActionType.Resume: {
            const { responses, availableQuestions } = state;
            const unansweredQuestionIds = availableQuestions.filter(
                _question => !responses[_question._id],
            );
            const activeQuestionIndex =
                unansweredQuestionIds.length > 0
                    ? availableQuestions.findIndex(
                          _question =>
                              _question._id === unansweredQuestionIds[0]._id,
                      )
                    : null;

            nextState = {
                ...state,
                activeQuestionIndex,
            };

            break;
        }

        case ActionType.UpdateActiveQuestionIndex: {
            const { responses, availableQuestions } = state;
            let { activeQuestionIndex } = state;

            if (
                questionIndex > 0 &&
                questionIndex < availableQuestions.length
            ) {
                const previousQuestion = availableQuestions[questionIndex - 1];
                const previousResponse = responses[previousQuestion._id];

                if (previousResponse) {
                    activeQuestionIndex = questionIndex;
                }
            }

            nextState = {
                ...state,
                activeQuestionIndex,
            };

            break;
        }
    }

    return nextState;
};

// initialize with copy of default quiz state
const initialState = {
    ...defaultQuizState,
};

// TODO: These quiz/question/logic related enums need to be consolidated to a single, global module. ILM 07-20-20
export enum LogicActionType {
    Load = "LOAD",
    SetFlag = "SET_FLAG",
    SetGlobalFlag = "SET_GLOBAL_FLAG",
    IncrementTally = "INCREMENT_TALLY",
    IncrementGlobalTally = "INCREMENT_GLOBAL_TALLY",
    Undo = "UNDO",
}

const logicReducer = (state, action) => {
    let nextState = state;
    let internalStream = state.internal.stream;
    let externalState = state.external;
    let didUpdate = false;

    switch (action.type) {
        case LogicActionType.Load: {
            externalState = action.externalState;
            internalStream = action.internalStream.length
                ? action.internalStream
                : internalStream;
            didUpdate = true;
            break;
        }

        case LogicActionType.Undo: {
            internalStream = internalStream.filter(streamAction => {
                const isMatch = streamAction.questionId === action.questionId;

                if (isMatch) {
                    didUpdate = true;
                }

                return !isMatch;
            });

            break;
        }

        case LogicActionType.SetFlag:
        case LogicActionType.SetGlobalFlag:
        case LogicActionType.IncrementTally:
        case LogicActionType.IncrementGlobalTally: {
            internalStream = [...state.internal.stream, action];
            didUpdate = true;
            break;
        }
    }

    if (didUpdate) {
        const internalState = internalStream.reduce(logicStateReducer, {
            local: {},
            global: {},
        });

        nextState = {
            ...state,
            external: {
                ...state.external,
                ...externalState,
            },
            internal: {
                ...state.internal,
                ...internalState,
                stream: internalStream,
            },
        };
    }

    return nextState;
};

// initialize with a copy of the default logic state
const initialLogicState = {
    ...defaultLogicState,
};

const Quiz = ({ route, navigation }) => {
    const [headerHeight, setHeaderHeight] = useState(
        drawer.BUTTON_HEIGHT + drawer.BUTTON_TOP,
    );
    const frame = useSafeAreaFrame();
    const { quizId, isReview = false } = route.params || {};
    const [{ isLoading: isQuizLoading, data: quiz }] = useQuiz(quizId);
    const {
        quizStates: externalQuizStates,
        logicStates: externalLogicStates,
        api: { addQuiz, removeQuizById },
    } = useExternalState();
    const { setLogicState, storeLogicState } = useContext(LogicStateContext);
    const furniture = useContext(FurnitureContext);
    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
    });
    const [logicState, logicDispatch] = useReducer(logicReducer, {
        ...initialLogicState,
    });
    const { activeQuestionIndex, availableQuestions, previousQuestionIndex } =
        state;
    const backgroundTranslateX = useRef(new Animated.Value(0)).current;
    const { name: themeName, backgroundParallax }: any = useTheme();
    const [isDispatching, setDispatching] = useState(false);

    // const { width: backgroundWidth, height: backgroundHeight } =
    // 	Image.resolveAssetSource(backgroundParallax);
    //

    let backgroundWidth, backgroundHeight;
    if (Platform.OS === "web") {
        backgroundWidth = 4500;
        backgroundHeight = 3248;
    } else {
        const assetSource = Image.resolveAssetSource(backgroundParallax);
        backgroundWidth = assetSource.width;
        backgroundHeight = assetSource.height;
    }
    const backgroundRatio = frame.height / backgroundHeight;

    useEffect(() => {
        if (externalLogicStates) {
            const internalStream = externalLogicStates
                .filter(externalLogicState => {
                    return externalLogicState.quizId === quizId;
                })
                .reduce((acc, externalLogicState) => {
                    return acc.concat(externalLogicState.stream);
                }, []);
            // load quiz state - score etc
            const externalLocalState = internalStream.reduce(
                logicStateReducer,
                {
                    local: {},
                    global: {},
                },
            );
            // load global state - global flags and tallies
            const externalGlobalState = externalLogicStates.reduce(
                (acc, logicState) => {
                    const result = logicState.stream.reduce(
                        logicStateReducer,
                        acc,
                    );

                    return result;
                },
                {
                    local: {},
                    global: {},
                },
            );
            const logicAction = {
                type: LogicActionType.Load,
                internalStream,
                externalState: {
                    local: externalLocalState.local,
                    global: externalGlobalState.global,
                },
            };

            logicDispatch(logicAction);
        }
        if (externalQuizStates) {
            const matches = externalQuizStates.filter(externalQuizState => {
                return externalQuizState.quizId === quizId;
            });
            if (matches.length) {
                const initAction = matches.reduce(
                    (acc, externalQuizState) => {
                        const {
                            responses,
                            activeQuestionIndex,
                            isComplete,
                            dateCompleted,
                        } = externalQuizState;
                        return {
                            ...acc,
                            responses,
                            activeQuestionIndex,
                            isComplete,
                            dateCompleted,
                        };
                    },
                    { ...initialState, type: ActionType.Init },
                );
                dispatch(initAction);
            }
        }
    }, [externalLogicStates, externalQuizStates]);

    useEffect(() => {
        if (!isQuizLoading && logicState.external) {
            const action = {
                type: ActionType.Load,
                quiz,
                logicState,
            };

            dispatch(action);
        }
    }, [isQuizLoading, logicState, state.responses]);

    useEffect(() => {
        if (availableQuestions?.length === 0) {
            navigation.goBack();
        }
    }, [availableQuestions]);

    useEffect(() => {
        if (availableQuestions) {
            const question = availableQuestions[activeQuestionIndex];

            if (Platform.OS !== "web") {
                const segmentCount = availableQuestions.length + 1;
                const segmentLength =
                    (backgroundWidth * backgroundRatio - frame.width) /
                    segmentCount;
                const segmentIndex = activeQuestionIndex + 1;
                const toValue = segmentIndex * segmentLength * -1;
                Animated.timing(backgroundTranslateX, {
                    duration: 300,
                    useNativeDriver: true,
                    toValue,
                }).start();
            }

            const interstitialCount = availableQuestions
                .slice(0, activeQuestionIndex)
                .filter(
                    _question => _question.type === QuestionType.Interstitial,
                ).length;
            const questionDisplayIndex =
                activeQuestionIndex - interstitialCount;

            if (activeQuestionIndex === -1) {
                navigation.navigate("QuizIntro");
            } else if (isReview) {
                navigation.navigate("QuizReview", { isReview });
            } else if (activeQuestionIndex === availableQuestions?.length) {
                if (!isDispatching) {
                    navigation.navigate("QuizReview", {
                        isReview: false,
                    });
                }
            } else {
                // if we are moving "up" the stack we 'push' to the next screen
                // else we 'navigate' allowing the navigator to control the animation
                const method =
                    activeQuestionIndex > previousQuestionIndex
                        ? "push"
                        : "navigate";
                navigation[method](`QuizQuestion_${question._id}`, {
                    index: questionDisplayIndex,
                    id: question._id,
                    count: state.questionDisplayCount,
                    quizType: quiz.type,
                });
            }
        }
    }, [availableQuestions?.length, activeQuestionIndex]);

    // end of quiz
    useEffect(() => {
        if (state.isComplete && !isReview) {
            // store quiz state upon completion
            addQuiz(state, logicState);
            // store logic state upon completion
            storeLogicState(logicState);
        }
    }, [state.isComplete]);

    /**
     * Whenever the logic state has been updated,
     * set the logic state in the LogicStateContext to be used
     * for RichText and in other places
     */
    useEffect(() => {
        setLogicState(logicState);
    }, [logicState]);

    const api = {
        start() {
            const action = {
                type: ActionType.Start,
            };
            dispatch(action);
        },

        next(responses: any[]) {
            responses?.forEach(response => {
                const {
                    questionId,
                    optionIds = [],
                    optionValues = [],
                } = response;
                const question =
                    availableQuestions.find(
                        _question => _question._id === questionId,
                    ) || response.question;

                const undoAction = {
                    type: LogicActionType.Undo,
                    questionId,
                    optionIds,
                };

                logicDispatch(undoAction);

                if (optionIds) {
                    const responseAction = {
                        type: ActionType.SubmitResponse,
                        questionId,
                        optionIds,
                    };

                    dispatch(responseAction);

                    if (
                        quiz.type === QuizType.KnowledgeTest ||
                        quiz.type === QuizType.SimpleChecklist
                    ) {
                        const optionId = optionIds[0];
                        const correctMatch = question?.correctAnswers?.find(
                            correctAnswer => correctAnswer._id === optionId,
                        );

                        if (correctMatch) {
                            const logicAction = {
                                type: LogicActionType.IncrementTally,
                                key: "DEFAULT",
                                value: 1,
                                questionId,
                                optionIds,
                            };

                            logicDispatch(logicAction);
                        }
                    }

                    if (question.type === QuestionType.Priority) {
                        optionIds.forEach((id, i) => {
                            let value = optionValues[i];
                            let logicAction = {
                                type: LogicActionType.IncrementTally,
                                key: id,
                                value,
                                questionId,
                                optionIds: [id],
                            };
                            logicDispatch(logicAction);
                        });
                    }

                    if (question.outcomes?.length > 0) {
                        question.outcomes.forEach(outcome => {
                            const shouldTrigger = outcome.triggers?.reduce(
                                (acc, trigger) => {
                                    if (acc) {
                                        return acc;
                                    }

                                    if (
                                        question.type === QuestionType.Priority
                                    ) {
                                        // if both values are equal, no winner, no trigger, no chicken dinner.
                                        if (
                                            optionValues.every(
                                                (v: number) =>
                                                    v === optionValues[0],
                                            )
                                        )
                                            return false;
                                        // else biggest option value wins
                                        let i = optionValues.indexOf(
                                            Math.max(...optionValues),
                                        );
                                        return (
                                            trigger.option._id === optionIds[i]
                                        );
                                    }

                                    const match = optionIds.find(
                                        _optionId =>
                                            _optionId === trigger.option._id,
                                    );

                                    return !!match;
                                },
                                false,
                            );

                            if (outcome.type !== "FOLLOW_UP") {
                                if (shouldTrigger) {
                                    let logicAction;

                                    switch (outcome.type) {
                                        case LogicActionType.SetFlag: {
                                            logicAction = {
                                                type: outcome.type,
                                                key: outcome.setFlagKey,
                                                value: outcome.setFlagValue,
                                                questionId,
                                                optionIds,
                                            };

                                            break;
                                        }

                                        case LogicActionType.SetGlobalFlag: {
                                            logicAction = {
                                                type: outcome.type,
                                                key: outcome.setGlobalFlagKey,
                                                value: outcome.setGlobalFlagValue,
                                                questionId,
                                                optionIds,
                                            };

                                            break;
                                        }

                                        case LogicActionType.IncrementTally: {
                                            logicAction = {
                                                type: outcome.type,
                                                key: outcome.incrementTallyKey,
                                                value: outcome.incrementTallyValue,
                                                questionId,
                                                optionIds,
                                            };

                                            break;
                                        }

                                        case LogicActionType.IncrementGlobalTally: {
                                            logicAction = {
                                                type: outcome.type,
                                                key: outcome.incrementGlobalTallyKey,
                                                value: outcome.incrementGlobalTallyValue,
                                                questionId,
                                                optionIds,
                                            };

                                            break;
                                        }
                                    }
                                    // guard against dynamic final questions triggering end of quiz before state is saved
                                    setDispatching(true);
                                    logicDispatch(logicAction);
                                    setDispatching(false);
                                }
                            }
                        });
                    }
                } else if (question.type === QuestionType.Interstitial) {
                    const responseAction = {
                        type: ActionType.SubmitResponse,
                        optionIds: [INTERSTITIAL_RESPONSE],
                        questionId,
                    };

                    dispatch(responseAction);
                }
            });

            const action = {
                type: ActionType.Next,
            };

            dispatch(action);
        },
        previous() {
            const action = {
                type: ActionType.Previous,
            };

            dispatch(action);
        },
        redo() {
            return removeQuizById(quiz._id);
        },
        getQuiz() {
            return quiz;
        },
        getQuestion(questionId: string) {
            return availableQuestions.find(
                question => question._id === questionId,
            );
        },
        getOptions(questionId?: string, question?: any) {
            const _question = question || api.getQuestion(questionId);
            const _defaultOptions = getDefaultOptionsForType(
                _question.type,
                furniture,
            );
            const _options = _question.options || [];
            const _additionalOptions = _question.additionalOptions || [];
            const options = [
                ..._defaultOptions,
                ..._options,
                ..._additionalOptions,
            ];

            return options;
        },
        logicState,
        quizState: state,
        headerHeight,
    };

    if (!availableQuestions) {
        return (
            <Container>
                <Background
                    source={backgroundParallax}
                    translateX={backgroundTranslateX}
                    width={backgroundWidth * backgroundRatio}
                    height={backgroundHeight * backgroundRatio}
                />
            </Container>
        );
    }

    const renderScreens = () => {
        const questions = availableQuestions.map(question => (
            <Stack.Screen
                key={`QuizQuestion_${question._id}`}
                name={`QuizQuestion_${question._id}`}
                component={QuizQuestion}
                options={{ gestureEnabled: false }}
            />
        ));
        return [
            <Stack.Screen
                name="QuizIntro"
                key="QuizIntro"
                component={QuizIntro}
                options={{ gestureEnabled: false }}
            />,
            ...questions,
            <Stack.Screen
                name="QuizReview"
                key="QuizReview"
                component={QuizReview}
                options={{ gestureEnabled: false }}
            />,
        ];
    };

    const { title, titleRich } = quiz;
    const theTitle = titleRich ? <InlineRichText doc={titleRich} /> : title;

    const titleTextShadow =
        themeName === "light"
            ? {}
            : {
                  textShadowColor: "#000000",
                  textShadowOffset: { width: 0, height: 1 },
                  textShadowRadius: 1,
              };

    return (
        <SafeAreaProvider>
            <QuizContext.Provider value={api}>
                <DrawerButton
                    onGoBack={() => {
                        if (
                            state.activeQuestionIndex === -1 ||
                            state.isComplete
                        ) {
                            navigation.goBack();
                        } else {
                            api.previous();
                        }
                    }}
                />
                <CloseButton onPress={() => navigation.goBack()} />
                <Container>
                    <Background
                        source={backgroundParallax}
                        translateX={backgroundTranslateX}
                        width={backgroundWidth * backgroundRatio}
                        height={backgroundHeight * backgroundRatio}
                    />
                    <Header
                        onLayout={e => {
                            setHeaderHeight(e.nativeEvent.layout.height);
                        }}>
                        <Title
                            type="h9"
                            allowFontScaling={false}
                            style={titleTextShadow}>
                            {theTitle}
                        </Title>
                    </Header>
                    <Stack.Navigator
                        initialRouteName="QuizIntro"
                        screenOptions={{
                            headerShown: false,
                            animationEnabled: true,
                            cardStyle: {
                                backgroundColor: "transparent",
                            },
                            cardStyleInterpolator: ({
                                current,
                                closing,
                                next,
                                layouts,
                            }) => {
                                let translateX;
                                let opacity;

                                if (next) {
                                    translateX = Animated.subtract(
                                        0,
                                        Animated.multiply(
                                            layouts.screen.width,
                                            next.progress,
                                        ),
                                    );

                                    opacity = Animated.subtract(
                                        1,
                                        next.progress,
                                    );
                                } else {
                                    translateX = Animated.subtract(
                                        layouts.screen.width,
                                        Animated.multiply(
                                            layouts.screen.width,
                                            current.progress,
                                        ),
                                    );

                                    opacity = current.progress;
                                }

                                return {
                                    containerStyle: {
                                        opacity,
                                        transform: [
                                            {
                                                translateX,
                                            },
                                        ],
                                    },
                                };
                            },
                            gestureEnabled: false,
                        }}>
                        {renderScreens()}
                    </Stack.Navigator>
                </Container>
            </QuizContext.Provider>
        </SafeAreaProvider>
    );
};

export default Quiz;
